import { useState } from "react";
import { admin } from "../../api/client";
import type { Beneficiary } from "../../api/types";
import { can, staffRole, useMe } from "../../auth/session";
import { TocaPage } from "../../components/Shell";
import { Empty, ErrorBox, Pill, Spinner, useToast } from "../../components/ui";
import { useAsync } from "../../hooks/useAsync";
import { fmt, t } from "../../i18n";
import { dateTimeShort } from "../../lib/format";
import { countryName, railDestination, railLabel } from "../../lib/rail";
import { DecisionModal } from "./shared";

const STATUSES = ["pending_review", "blocked", "active"];
const TONE: Record<string, "ok" | "warn" | "crit"> = { active: "ok", pending_review: "warn", blocked: "crit" };

export function TocaFornecedores() {
  const me = useMe();
  const toast = useToast();
  const [status, setStatus] = useState("pending_review");
  const { data, error, loading, reload } = useAsync(() => admin.beneficiaries(status), [status]);
  const [deciding, setDeciding] = useState<Beneficiary | null>(null);
  const allowed = can.decideBeneficiary(staffRole(me));

  return (
    <TocaPage title={t.toca.benTitle}>
      <div className="chips" role="tablist">
        {STATUSES.map((s) => (
          <button key={s} type="button" className="chip" role="tab" aria-selected={status === s} onClick={() => setStatus(s)}>
            {t.suppliers.status[s]}
          </button>
        ))}
      </div>
      <div className="card">
        {loading && !data ? (
          <Spinner />
        ) : error ? (
          <div style={{ padding: 16 }}><ErrorBox error={error} onRetry={reload} /></div>
        ) : !data || data.length === 0 ? (
          <Empty body={t.toca.benEmpty} />
        ) : (
          <div className="tscroll" tabIndex={0}>
            <table>
              <thead>
                <tr>
                  <th>{t.suppliers.colName}</th>
                  <th>{t.toca.company}</th>
                  <th>{t.suppliers.colRail}</th>
                  <th>{t.suppliers.flagsTitle}</th>
                  <th>{t.common.status}</th>
                  <th>{t.common.since}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.map((b) => (
                  <tr key={b.id}>
                    <td>
                      <div className="cell2">
                        <b>{b.legalName}</b>
                        <span>{countryName(b.country)} · {t.suppliers.relationships[b.relationship] ?? b.relationship}</span>
                      </div>
                    </td>
                    <td className="ink2">{b.company?.legalName ?? b.companyId ?? "—"}</td>
                    <td>
                      <div className="cell2">
                        <b style={{ fontWeight: 500 }}>{railLabel(b.rail)}</b>
                        <span className="mono">{b.rail.kind === "stablecoin" ? b.rail.address : railDestination(b.rail)}</span>
                      </div>
                    </td>
                    <td>
                      <div className="row" style={{ gap: 6 }}>
                        {(b.screeningFlags ?? []).map((f) => <Pill key={f} tone="warn">{f}</Pill>)}
                        {!(b.screeningFlags ?? []).length && <span className="muted">—</span>}
                      </div>
                    </td>
                    <td><Pill tone={TONE[b.status] ?? "warn"}>{t.suppliers.status[b.status] ?? b.status}</Pill></td>
                    <td className="muted" style={{ whiteSpace: "nowrap" }}>{dateTimeShort(b.dataChangedAt ?? b.createdAt)}</td>
                    <td className="r">
                      {allowed && <button type="button" className="btn dark xs" onClick={() => setDeciding(b)}>{t.toca.kybDecide}</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {!allowed && <span className="muted small">{t.common.readonlyHint}</span>}
      {deciding && (
        <DecisionModal
          title={`${t.toca.kybDecide} · ${deciding.legalName}`}
          subject={`${countryName(deciding.country)} · ${railLabel(deciding.rail)} · ${deciding.rail.kind === "stablecoin" ? deciding.rail.address : railDestination(deciding.rail)}`}
          options={[
            { value: "active" as const, label: t.toca.benDecision.active! },
            { value: "blocked" as const, label: t.toca.benDecision.blocked!, danger: true },
          ]}
          onDecide={async (d, note) => {
            await admin.beneficiaryDecide(deciding.id, d, note);
            toast(fmt(t.toca.benDecided, { d: t.toca.benDecision[d] }));
            void reload();
          }}
          onClose={() => setDeciding(null)}
        />
      )}
    </TocaPage>
  );
}
